// Ladeanimation mit dem Hof-Wappen.
//
// Kein Blinken, kein Spinner-Kreisel: Das Wappen schnappt herein, landet mit
// leichtem Überschwingen und ATMET dann ruhig weiter, während der Ring läuft.
//
// Der Ring ist kein Deko-Element — er dreht ununterbrochen und ist das
// eigentliche "es lädt noch"-Signal. Das Wappen darf stehen, der Ring läuft.
//
// ── Was hier verlorenging, und warum
//
// Vorher baute sich das Emblem Teil für Teil auf (Kreis schnappt ein, Formen
// ploppen versetzt, Schriftzug steigt auf). Das ging nur, weil die Vorlage ein
// SVG war, dessen einzelne Pfade man einzeln animieren kann. Das echte Hofwappen
// gibt es als Bilddatei — ein Bild hat keine Teile. Es nachzuzeichnen wäre ein
// ZWEITES Logo gewesen, das beim nächsten Feinschliff am Original vorbeiläuft.
// Lieber die schlichtere Bewegung mit dem richtigen Wappen als die schönere mit
// dem falschen. Kommt das Logo je als Vektor, lebt die alte Choreografie in der
// Versionsgeschichte weiter.

import logoBild from "@/assets/logo-marke.png";
import { cn } from "@/design-system/ui";

const SIZES = {
  sm: "h-16 w-16",
  md: "h-28 w-28",
  lg: "h-40 w-40",
} as const;

export function LogoLoader({
  size = "md",
  label,
  className,
}: {
  size?: keyof typeof SIZES;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center gap-5", className)}>
      <div className={cn("relative grid place-items-center", SIZES[size])}>
        {/* Fortschrittsring — läuft durch, solange geladen wird */}
        <svg
          className="absolute inset-0 h-full w-full -rotate-90 hs-ring"
          viewBox="0 0 100 100"
          aria-hidden
        >
          <circle
            cx="50"
            cy="50"
            r="47"
            fill="none"
            className="stroke-ink-700"
            strokeWidth="2"
          />
          <circle
            cx="50"
            cy="50"
            r="47"
            fill="none"
            className="stroke-gruen-500 hs-ring-arc"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>

        {/* Das Wappen selbst */}
        <img
          src={logoBild}
          alt=""
          role="img"
          aria-label={label ?? "Wird geladen"}
          className="hs-logo h-[76%] w-[76%] object-contain"
        />
      </div>

      {label && (
        <p className="text-sm font-medium tabular-nums text-ink-400">{label}</p>
      )}

      <style>{CSS}</style>
    </div>
  );
}

/** Vollbild-Ladezustand — beim App-Start und bei langen Wartezeiten. */
export function FullscreenLoader({ label }: { label?: string }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950">
      <LogoLoader size="lg" label={label} />
    </div>
  );
}

// Die Choreografie. Ein Zyklus dauert 3,4 s: hereinschnappen (~0,5 s), atmen,
// zum Schluss leicht zurücknehmen und neu ansetzen.
const CSS = `
.hs-logo {
  transform-origin: center;
  animation: hs-pop 3.4s cubic-bezier(.34,1.56,.64,1) infinite;
}

@keyframes hs-pop {
  0%       { transform: scale(0) rotate(-14deg); opacity: 0; }
  16%      { transform: scale(1) rotate(0deg);   opacity: 1; }
  /* Atmen, solange noch geladen wird */
  55%      { transform: scale(1.04); }
  75%      { transform: scale(1); }
  92%      { transform: scale(1); opacity: 1; }
  100%     { transform: scale(.94); opacity: 0; }
}

/* Der Ring: dreht endlos und zieht seinen Bogen auf und zu */
.hs-ring { animation: hs-spin 2s linear infinite; }
.hs-ring-arc {
  stroke-dasharray: 295;
  animation: hs-arc 1.6s ease-in-out infinite;
}
@keyframes hs-spin { to { transform: rotate(270deg); } }
@keyframes hs-arc {
  0%   { stroke-dashoffset: 280; }
  50%  { stroke-dashoffset: 90;  }
  100% { stroke-dashoffset: 280; }
}

@media (prefers-reduced-motion: reduce) {
  .hs-logo { animation: none; }
  .hs-ring { animation: hs-spin 3s linear infinite; }
  .hs-ring-arc { animation: none; stroke-dashoffset: 200; }
}
`;
